import React from 'react';
import { LockOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { VisitAccessPoint } from 'services/access-points/access-points.service';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { EntityPicker, groupItems, ItemGroup, PickerItem } from 'components/entity-picker/entity-picker';
import { capabilitiesFor } from './point-capabilities';

export interface AccessPointPickerProps {
    points: VisitAccessPoint[];
    selectedId?: string;
    onSelect: (point: VisitAccessPoint) => void;
    onClose: () => void;
}

export interface SiteGroup {
    siteName: string;
    points: VisitAccessPoint[];
}

const keyOf = (p: VisitAccessPoint) => `${p.kind}-${p.id}`;

// Se conserva la firma vieja (sitios y puntos) porque es la que ejercitan los
// tests de no-regresión: por dentro delega en el picker genérico.
export function groupBySite(points: VisitAccessPoint[], query: string): SiteGroup[] {
    const byKey = new Map(points.map((p) => [keyOf(p), p]));
    return groupItems(
        points.map((p) => ({
            key: keyOf(p),
            id: p.id,
            groupName: p.site_name,
            title: p.name,
            subtitle: '',
        })),
        query,
    ).map((g: ItemGroup) => ({
        siteName: g.groupName,
        points: g.items.map((i) => byKey.get(i.key) as VisitAccessPoint),
    }));
}

export const AccessPointPicker: React.FC<AccessPointPickerProps> = ({ points, selectedId, onSelect, onClose }) => {
    const { translate } = useTranslator();
    const t = (key: string) => translate({ key });
    const byKey = new Map(points.map((p) => [keyOf(p), p]));

    // El backend manda name vacío para recepción a propósito: ese label es texto de UI.
    const items: PickerItem[] = points.map((p) => ({
        key: keyOf(p),
        id: p.id,
        groupName: p.site_name,
        title: p.kind === 'reception' ? t('visit-access.picker-reception') : p.name,
        subtitle: t(capabilitiesFor(p.kind).pickerSubtitleKey),
        icon: p.kind === 'reception' ? <UserSwitchOutlined aria-hidden /> : <LockOutlined aria-hidden />,
    }));

    return (
        <EntityPicker
            items={items}
            selectedId={selectedId}
            title={t('visit-access.picker-title')}
            searchPlaceholder={t('visit-access.picker-search')}
            backLabel={t('visit-access.back')}
            emptyLabel={t('visit-access.picker-no-results')}
            onSelect={(item) => onSelect(byKey.get(item.key) as VisitAccessPoint)}
            onClose={onClose}
        />
    );
};
